import type {
  IntrinsicSystemProps,
  LabeledVariants,
  MotionPropsFor,
} from '@hover/blueprint';
import { useImage } from '@hover/blueprint/chakra';
import { includes } from '@hover/utilities';
import { useState } from 'react';

import { useAiGeneratedWatermarkImageQuery } from 'src/shared/graphql/generated';
import { isServer } from 'src/shared/helpers/environment';

import { isGeneratingImage } from '../helpers';
import type { AiOrSampleGeneratedImage } from '../types';
import { isSample } from '../types';

import type { UseGeneratedImagePollingParams } from './useGenerateImageHandler';
import { useGeneratedImagePolling } from './useGenerateImageHandler';
import { useHomeownerAppContext } from './useHomeownerAppContext';

export interface UseGenerateImageLoadingStateOptions
  extends Pick<UseGeneratedImagePollingParams, 'consumerId'> {
  image: AiOrSampleGeneratedImage;
  watermarkImage?: boolean;
}

export const imageVariants = {
  loading: 'loading',
  generating: 'generating',
  loaded: 'loaded',
} as const;

export type ImageVariant = (typeof imageVariants)[keyof typeof imageVariants];

export const variants: LabeledVariants<ImageVariant> = {
  loading: {
    opacity: 0,
    filter: 'brightness(1) saturate(1) blur(0px)',
  },
  generating: {
    opacity: 0,
    filter: 'brightness(1) saturate(0) blur(10px)',
  },
  loaded: {
    opacity: 1,
    filter: 'brightness(1) saturate(1) blur(0px)',
  },
};

/**
 * Poll for a generated image to be ready while tracking the loading state and
 * providing props to animate in the image element that displays the image
 *
 * @returns loading state, animation state and image props to spread into the
 * image element
 */
export const useGenerateImageLoadingState = ({
  image,
  consumerId,
  watermarkImage,
}: UseGenerateImageLoadingStateOptions) => {
  const { generatedImagePath } = image;

  const { user } = useHomeownerAppContext();
  const isGenerating = isGeneratingImage(image);
  // Only show the watermark for pro users
  const watermark =
    watermarkImage && user && !user.isHomeowner && !isSample(image);
  const { data: watermarkImageData } = useAiGeneratedWatermarkImageQuery({
    variables: {
      id: isSample(image) ? '' : String(image.id),
    },
    skip: isGenerating || !watermark,
  });

  const watermarkImageUrl =
    watermarkImageData?.aiGeneratedWatermarkImage?.watermarkImageUrl ?? '';

  const imageProps = {
    src: generatedImagePath,
  } as const;
  const imageState = useImage(imageProps);
  const watermarkImageProps = {
    src: watermarkImageUrl,
  } as const;
  const watermarkImageState = useImage(watermarkImageProps);
  const isLoadingImage =
    isServer() || includes(['pending', 'loading'], imageState);
  const isWatermarkLoaded =
    isServer() || includes(['loaded'], watermarkImageState);

  // Show the loading state when we're generating a new image,
  // until the generated image has loaded and animated in.
  // The watermark image will pop in when it's loaded.
  const [wasGenerating, setWasGenerating] = useState(isGenerating);
  const isGeneratedLoading = isGenerating || (isLoadingImage && wasGenerating);
  if (wasGenerating && !isGeneratedLoading) setWasGenerating(false);

  const polling = useGeneratedImagePolling({
    consumerId,
    image,
  });

  return {
    ...polling,
    isGeneratedLoading,
    isLoadingImage,
    imageProps: {
      ...(watermark && isWatermarkLoaded ? watermarkImageProps : imageProps),
      variants,
    } as const satisfies MotionPropsFor<IntrinsicSystemProps<'img'>>,
    imageVariants,
  };
};
